import React, {useCallback, useContext, useMemo, useState} from "react";
import {ConfigProvider, DatePicker, TimePicker, Tooltip} from "antd";
import {QuestionCircleOutlined} from "@ant-design/icons";
import moment from "moment";
import locale from "antd/lib/locale/he_IL";
import "./date-time-filter-popover.css";
import {MarketplaceFilterProviderContext} from "../../MarketplaceFilterContext";
import {BasePopoverFilter} from "../basePopoverFilter.jsx";
import {AppContext} from "../../../../AppContext.jsx";
import {isEqual} from "lodash";
import {
    dateTimeFilterTitles,
    DEFAULT_DATE_TIME_FILTER_TITLE,
    DATE_TIME_LABELS,
    MIN_TIME_HOUR,
    MAX_TIME_HOUR,
    MAX_TIME_MINUTE,
    FIRST_DISABLED_MINUTE_AT_23,
    DISABLED_MINUTES_COUNT_AT_23,
    DEFAULT_MINUTE_STEP,
    MINIMUM_ORDER_NOTICE_HOURS,
    LAST_MINUTE
} from "./consts.jsx";
import {StyledButton} from "../../../../components/StyledButton.jsx";
import {getDateTimeFilterTitle, validateDateTimeFilter} from "./utils.js";
import classnames from "classnames";

export const DateTimeFilterPopover = () => {
    const {filterValues} = useContext(AppContext);
    const {
        innerState,
        updateInnerState,
        removeKeyFromInnerState,
        panel,
        setInnerState,
        dateTimeFilterTitle,
        setDateTimeFilterTitle
    } = useContext(MarketplaceFilterProviderContext);
    const [timePickerOpen, setTimePickerOpen] = useState(false);

    const popoverTitle = dateTimeFilterTitles[panel] || DEFAULT_DATE_TIME_FILTER_TITLE;

    // Get values from innerState
    const eventDate = useMemo(
        () => (innerState?.dateTimeFilter?.eventDate ? moment(innerState.dateTimeFilter.eventDate) : null),
        [innerState?.dateTimeFilter?.eventDate]
    );
    const maxTime = useMemo(
        () => (innerState?.dateTimeFilter?.maxTime ? moment(innerState.dateTimeFilter.maxTime) : null),
        [innerState?.dateTimeFilter?.maxTime]
    );

    const errors = useMemo(() => validateDateTimeFilter(eventDate, maxTime), [eventDate, maxTime]);

    const hasError = useMemo(() => !!Object.keys(errors ?? {})?.length, [errors]);

    const handleTimeSelect = useCallback(
        time => {
            if (time) {
                const currentTime = maxTime;
                updateInnerState("dateTimeFilter.maxTime", time.valueOf());
                // Close picker only if both hour and minute are selected (minute changed)
                if (currentTime && time.minute() !== currentTime.minute()) {
                    setTimePickerOpen(false);
                }
            } else {
                removeKeyFromInnerState("dateTimeFilter.maxTime");
            }
        },
        [maxTime, updateInnerState, removeKeyFromInnerState, setTimePickerOpen]
    );

    const didStateChange = useMemo(() => {
        const isDateTimeFilterEqual = isEqual(innerState?.dateTimeFilter, filterValues?.dateTimeFilter);
        return !isDateTimeFilterEqual;
    }, [innerState?.dateTimeFilter, filterValues?.dateTimeFilter]);

    // Disable hours before MIN_TIME_HOUR and minutes after MAX_TIME_MINUTE at MAX_TIME_HOUR
    // If date is today, hide hours before now and disable hours within 3 hours from now
    const getDisabledTime = useCallback(() => {
        const now = moment();
        const isToday = eventDate && eventDate.isSame(now, "day");

        if (!isToday) {
            return {
                disabledHours: () => Array.from({length: MIN_TIME_HOUR}, (_, i) => i),
                disabledMinutes: selectedHour =>
                    selectedHour === MAX_TIME_HOUR
                        ? Array.from({length: DISABLED_MINUTES_COUNT_AT_23}, (_, i) => i + FIRST_DISABLED_MINUTE_AT_23)
                        : []
            };
        }

        // Today: hide hours before now and disable hours within 3 hours from now
        const currentHour = now.hour();
        const currentMinute = now.minute();
        const minimumNoticeTime = now.clone().add(MINIMUM_ORDER_NOTICE_HOURS, "hours");
        const minimumNoticeHour = minimumNoticeTime.hour();
        const minimumNoticeMinute = minimumNoticeTime.minute();

        return {
            disabledHours: () => {
                const disabled = new Set();
                // Disable 0-6
                for (let i = 0; i < MIN_TIME_HOUR; i++) disabled.add(i);
                // Disable hours before current hour
                for (let i = MIN_TIME_HOUR; i < currentHour; i++) disabled.add(i);
                // Disable hours within 3 hours from now
                for (let i = currentHour; i < minimumNoticeHour; i++) disabled.add(i);
                return Array.from(disabled);
            },
            disabledMinutes: selectedHour => {
                const disabled = [];
                if (selectedHour === MAX_TIME_HOUR) {
                    disabled.push(
                        ...Array.from({length: DISABLED_MINUTES_COUNT_AT_23}, (_, i) => i + FIRST_DISABLED_MINUTE_AT_23)
                    );
                }
                if (selectedHour === currentHour) {
                    disabled.push(...Array.from({length: currentMinute}, (_, i) => i));
                }
                if (selectedHour === minimumNoticeHour) {
                    disabled.push(...Array.from({length: minimumNoticeMinute}, (_, i) => i));
                }
                if (selectedHour > currentHour && selectedHour < minimumNoticeHour) {
                    disabled.push(...Array.from({length: LAST_MINUTE + 1}, (_, i) => i));
                }
                return disabled;
            }
        };
    }, [eventDate]);

    const onTomorrowClick = useCallback(() => {
        const tomorrow = moment().add(1, "day");
        updateInnerState("dateTimeFilter.eventDate", tomorrow.valueOf());
    }, [updateInnerState]);

    const onClear = useCallback(() => {
        setInnerState(({dateTimeFilter, ...otherProperties}) => ({...otherProperties}));
    }, [setInnerState]);

    const onClosePopover = useCallback(() => {
        if (!!hasError) {
            return;
        }

        const title = getDateTimeFilterTitle({
            eventDate: eventDate?.valueOf(),
            maxTime: maxTime?.valueOf()
        });
        setDateTimeFilterTitle(title);
    }, [eventDate, maxTime, setDateTimeFilterTitle, hasError]);

    return (
        <div>
            <ConfigProvider direction="rtl" locale={locale}>
                <BasePopoverFilter
                    onClear={onClear}
                    searchOnClose={(didStateChange && !hasError) || (didStateChange && !innerState?.dateTimeFilter)}
                    onClosePopover={onClosePopover}
                    className={classnames("date-time-filter-popover", panel)}
                    title={popoverTitle}
                    content={
                        <ConfigProvider direction="rtl">
                            <div id="date-time-filter-popover-content">
                                <div className="date-time-filter-form">
                                    <div
                                        className={classnames("date-time-filter-item", {
                                            "date-time-filter-item-error": errors?.eventDate
                                        })}>
                                        <div className="date-time-filter-label">
                                            {DATE_TIME_LABELS.eventDate}
                                            <span className="date-time-filter-required">*</span>
                                        </div>
                                        <DatePicker
                                            value={eventDate}
                                            onChange={date =>
                                                date
                                                    ? updateInnerState("dateTimeFilter.eventDate", date.valueOf())
                                                    : removeKeyFromInnerState("dateTimeFilter.eventDate")
                                            }
                                            format="DD/MM/YYYY"
                                            status={errors?.eventDate ? "error" : undefined}
                                            placeholder={DATE_TIME_LABELS.eventDatePlaceholder}
                                            disabledDate={current =>
                                                current && current.isSameOrBefore(moment().startOf("day"), "day")
                                            }
                                            className="date-time-filter-date-picker"
                                        />
                                    </div>

                                    <div className="date-time-filter-item">
                                        <div className="date-time-filter-label">{DATE_TIME_LABELS.maxTime}</div>
                                        <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
                                            <TimePicker
                                                value={maxTime}
                                                open={timePickerOpen}
                                                onOpenChange={setTimePickerOpen}
                                                onSelect={handleTimeSelect}
                                                placeholder={DATE_TIME_LABELS.maxTimePlaceholder}
                                                className="date-time-filter-time-picker"
                                                minuteStep={DEFAULT_MINUTE_STEP}
                                                disabledTime={getDisabledTime}
                                                format="HH:mm"
                                                inputReadOnly={true}
                                                showNow={true}
                                            />
                                            <Tooltip title={DATE_TIME_LABELS.maxTimeTooltip} placement="bottom">
                                                <QuestionCircleOutlined className="date-time-filter-help-icon" />
                                            </Tooltip>
                                        </div>
                                    </div>
                                </div>

                                {hasError && <div className="date-time-filter-error">{errors.maxTime}</div>}

                                <div className="date-time-filter-actions">
                                    <StyledButton onClick={onTomorrowClick} className="tomorrow-button">
                                        {DATE_TIME_LABELS.tomorrowButton}
                                    </StyledButton>
                                </div>
                            </div>
                        </ConfigProvider>
                    }>
                    <div className="advanced-filter-segmented-label-container">
                        <div className="advanced-filter-option-title">{popoverTitle}</div>
                        <div
                            className={classnames("advanced-filter-option-sub-title", {
                                selected: !!dateTimeFilterTitle
                            })}>
                            {dateTimeFilterTitle ?? "בחר תאריך ושעה"}
                        </div>
                    </div>
                </BasePopoverFilter>
            </ConfigProvider>
        </div>
    );
};
